import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Popover } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import type { ClientSummary } from '@/features/Clients/Data';
import { SEM_PROJETO } from '../solicitacoesResumo.utils';

interface ProjetoFiltroDropdownProps {
  clientes: ClientSummary[];
  /** Por `chaveCliente` (clienteId ou SEM_PROJETO) — ver contarResumo. */
  contagem: Map<string, number>;
  selecionados: string[];
  onChange: (selecionados: string[]) => void;
}

const CHECKBOX_CLASS = 'h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500';

/**
 * Projeto (Cliente) pode ter dezenas de opções — em pills, como tipo/etapa, virava uma
 * parede. Aqui é um dropdown com busca e checkbox (1 ou mais), mais movimentados primeiro.
 * Mesma regra das pills: só aparece o que tem contagem ou já está selecionado.
 */
export default function ProjetoFiltroDropdown({ clientes, contagem, selecionados, onChange }: ProjetoFiltroDropdownProps) {
  const [busca, setBusca] = useState('');

  const opcoes = useMemo(() => {
    const nomePorId = new Map(clientes.map((cliente) => [cliente.id, cliente.name]));
    const chaves = new Set([...Array.from(contagem.keys()), ...selecionados]);
    return Array.from(chaves)
      .map((chave) => ({
        chave,
        nome: chave === SEM_PROJETO ? 'Sem projeto' : nomePorId.get(chave) ?? 'Projeto não encontrado',
        quantidade: contagem.get(chave) ?? 0,
      }))
      .sort((a, b) => b.quantidade - a.quantidade || a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [clientes, contagem, selecionados]);

  const opcoesFiltradas = busca.trim()
    ? opcoes.filter((opcao) => opcao.nome.toLowerCase().includes(busca.trim().toLowerCase()))
    : opcoes;

  const rotuloBotao =
    selecionados.length === 0
      ? 'Todos'
      : selecionados.length === 1
        ? opcoes.find((opcao) => opcao.chave === selecionados[0])?.nome ?? '1 selecionado'
        : `${selecionados.length} selecionados`;

  return (
    <div className="flex items-center gap-2">
      <Popover className="relative">
        <Popover.Button
          className={clsx(
            'flex min-w-[10rem] max-w-[16rem] items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm',
            selecionados.length > 0
              ? 'border-blue-600 bg-blue-50 font-medium text-blue-700'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          )}
        >
          <span className="truncate">{rotuloBotao}</span>
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
        </Popover.Button>
        <Popover.Panel className="absolute right-0 z-20 mt-1 w-72 rounded-md border border-gray-200 bg-white p-2 shadow-lg">
          <input
            type="text"
            className="input-primary mb-2 w-full"
            placeholder="Buscar projeto..."
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
          />
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {opcoesFiltradas.length === 0 && <p className="p-2 text-xs text-gray-400">Nenhum projeto.</p>}
            {opcoesFiltradas.map((opcao) => (
              <label
                key={opcao.chave}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className={CHECKBOX_CLASS}
                  checked={selecionados.includes(opcao.chave)}
                  onChange={(event) =>
                    onChange(
                      event.target.checked
                        ? [...selecionados, opcao.chave]
                        : selecionados.filter((chave) => chave !== opcao.chave)
                    )
                  }
                />
                <span className="flex-1 truncate">{opcao.nome}</span>
                <span className="text-xs text-gray-400">({opcao.quantidade})</span>
              </label>
            ))}
          </div>
        </Popover.Panel>
      </Popover>
    </div>
  );
}
